import { Plugin } from '@ckeditor/ckeditor5-core';
import { TwoStepCaretMovement as twostepcaretmovement } from '@ckeditor/ckeditor5-typing';
import InsertInternalLinkCommand from './internal-link-command';
import { findLinkRange } from './utils';
const HIGHLIGHT_CLASS = 'internal-link--selected';
export default class InternalLinkEditing extends Plugin {
    init() {
        const editor = this.editor;
        editor.model.schema.extend('$text', { allowAttributes: 'internalLink' });
        editor.commands.add('insertInternalLink', new InsertInternalLinkCommand(this.editor));
        this._defineConverters();
        editor.plugins.get(twostepcaretmovement).registerAttribute('internalLink');
        this._setupLinkHighlight();
        this._setupShortcut();
    }
    _defineConverters() {
        const editor = this.editor;
        editor.conversion.for('upcast').elementToAttribute({
            view: {
                name: 'x-internal-link',
            },
            model: {
                key: 'internalLink',
                value: (viewElement) => {
                    return {
                        dataPostId: viewElement.getAttribute('data-post-id'),
                        dataExampleId: viewElement.getAttribute('data-example-id'),
                        dataLinkType: viewElement.getAttribute('data-link-type'),
                        dataResourceId: viewElement.getAttribute('data-resource-id'),
                    };
                },
            },
        });
        editor.conversion.for('dataDowncast').attributeToElement({
            model: 'internalLink',
            view: (dataAttributes, { writer }) => {
                const eltAttributes = this._getElementAttributesByData(dataAttributes);
                if (!eltAttributes)
                    return null;
                const element = writer.createAttributeElement('x-internal-link', eltAttributes, {
                    priority: 5,
                });
                return element;
            },
        });
        editor.conversion.for('editingDowncast').attributeToElement({
            model: 'internalLink',
            view: (dataAttributes, { writer }) => {
                const eltAttributes = this._getElementAttributesByData(dataAttributes);
                if (!eltAttributes)
                    return null;
                const element = writer.createAttributeElement('span', eltAttributes, {
                    priority: 5,
                });
                writer.setCustomProperty('internalLink', true, element);
                return element;
            },
        });
    }
    _getElementAttributesByData(dataAttributes) {
        if (!dataAttributes)
            return null;
        const { dataLinkType, dataPostId, dataExampleId } = dataAttributes;
        switch (dataLinkType) {
            case 'example': // support for existing links
            case 'resume-example':
            case 'cover-letter-example':
            case 'post':
                return this.attributesForPublication(dataAttributes);
            case 'blog-category':
            case 'resume-example-category':
            case 'cover-letter-example-category':
            case 'resume-template-category':
            case 'cover-letter-template-category':
                return this.attributesForCategory(dataAttributes);
            case 'home':
            case 'resume-builder-landing':
            case 'cover-letter-builder-landing':
            case 'blog-root':
            case 'resume-example-root':
            case 'cover-letter-example-root':
            case 'create-resume':
            case 'create-cover-letter':
                return this.attributesForStaticPages(dataAttributes);
        }
        // Support for old internal links without dataLinkType
        if (dataPostId || dataExampleId) {
            return this.attributesForPublication(dataAttributes);
        }
    }
    attributesForPublication(attributes) {
        const { dataLinkType, dataPostId, dataExampleId } = attributes;
        const idKey = dataPostId ? 'data-post-id' : 'data-example-id';
        let htmlClass = '';
        // support for existing links
        // publication could be saved with data-link-type: "undefined"
        if (!dataLinkType || dataLinkType === 'undefined') {
            htmlClass = dataPostId ? 'internal-link-post' : 'internal-link-example';
        }
        else {
            htmlClass = {
                post: 'internal-link-post',
                example: 'internal-link-example', // support for existing links
                'resume-example': 'internal-link-resume-example',
                'cover-letter-example': 'internal-link-cover-letter-example',
            }[dataLinkType] || '';
        }
        return {
            'data-link-type': dataLinkType,
            [idKey]: dataPostId || dataExampleId,
            class: `internal-link ${htmlClass}`,
        };
    }
    attributesForCategory(attributes) {
        const { dataLinkType, dataResourceId } = attributes;
        const htmlClass = {
            'blog-category': 'internal-link-blog-category',
            'resume-example-category': 'internal-link-resume-example-category',
            'cover-letter-example-category': 'internal-link-cover-letter-example-category',
            'resume-template-category': 'internal-link-resume-template-category',
            'cover-letter-template-category': 'internal-link-cover-letter-template-category',
        }[dataLinkType];
        return {
            'data-link-type': dataLinkType,
            'data-resource-id': dataResourceId,
            class: `internal-link ${htmlClass}`,
        };
    }
    attributesForStaticPages(attributes) {
        const { dataLinkType } = attributes;
        return {
            'data-link-type': dataLinkType,
            class: 'internal-link internal-link-landing',
        };
    }
    _setupLinkHighlight() {
        const editor = this.editor;
        const view = editor.editing.view;
        const highlightedLinks = new Set();
        // Adding the class.
        view.document.registerPostFixer((writer) => {
            const selection = editor.model.document.selection;
            let changed = false;
            if (selection.hasAttribute('internalLink')) {
                const modelRange = findLinkRange(selection.getFirstPosition(), selection.getAttribute('internalLink'), editor.model);
                const viewRange = editor.editing.mapper.toViewRange(modelRange);
                // There might be multiple `a` elements in the `viewRange`, for example, when the `a` element is
                // broken by a UIElement.
                for (const item of viewRange.getItems()) {
                    if (item.is('node') && !item.hasClass(HIGHLIGHT_CLASS)) {
                        writer.addClass(HIGHLIGHT_CLASS, item);
                        highlightedLinks.add(item);
                        changed = true;
                    }
                }
            }
            return changed;
        });
        // Removing the class.
        editor.conversion.for('editingDowncast').add((dispatcher) => {
            // Make sure the highlight is removed on every possible event, before conversion is started.
            dispatcher.on('insert', removeHighlight, { priority: 'highest' });
            dispatcher.on('remove', removeHighlight, { priority: 'highest' });
            dispatcher.on('attribute', removeHighlight, { priority: 'highest' });
            dispatcher.on('selection', removeHighlight, { priority: 'highest' });
            function removeHighlight() {
                view.change((writer) => {
                    for (const item of highlightedLinks.values()) {
                        writer.removeClass(HIGHLIGHT_CLASS, item);
                        highlightedLinks.delete(item);
                    }
                });
            }
        });
    }
    _setupShortcut() {
        this.editor.keystrokes.set('Ctrl+Shift+K', 'insertInternalLink');
    }
}
